import type { HotkeyAction } from '../../engine/api';
import type { Actions } from '../actions';
import type { StudioApi } from '../../engine/api';

export interface BuiltinAction extends HotkeyAction {
  defaults: readonly string[];
  group: 'Edit' | 'Transform' | 'Scenes' | 'Output' | 'General';
}

const mod = (rest: string): string[] => [`Ctrl+${rest}`, `Meta+${rest}`];

/** Actions the interface itself provides. Plugin actions arrive through runtime.hotkeyActions. */
export function createBuiltinActions(studio: StudioApi, actions: Actions): BuiltinAction[] {
  const list: BuiltinAction[] = [
    { id: 'edit.undo', label: 'Undo', group: 'Edit', defaults: mod('KeyZ'), run: () => studio.project.undo() },
    { id: 'edit.redo', label: 'Redo', group: 'Edit', defaults: [...mod('Shift+KeyZ'), 'Ctrl+KeyY'], run: () => studio.project.redo() },
    { id: 'edit.delete', label: 'Remove selected items', group: 'Edit', defaults: ['Delete', 'Backspace'], run: () => actions.removeItems() },
    { id: 'edit.duplicate', label: 'Duplicate selected items', group: 'Edit', defaults: mod('KeyD'), run: () => actions.duplicate('reference') },
    { id: 'edit.copy', label: 'Copy items', group: 'Edit', defaults: mod('KeyC'), run: () => actions.copyItems() },
    { id: 'edit.paste', label: 'Paste items', group: 'Edit', defaults: mod('KeyV'), run: () => actions.pasteItems() },
    { id: 'edit.select-all', label: 'Select all items', group: 'Edit', defaults: mod('KeyA'), run: () => actions.selectAll() },
    { id: 'item.toggle-visibility', label: 'Show / hide selected items', group: 'Edit', defaults: ['KeyH'], run: () => actions.toggleVisibility() },

    { id: 'nudge.left', label: 'Nudge left 1 px', group: 'Transform', defaults: ['ArrowLeft'], run: () => actions.nudge(-1, 0) },
    { id: 'nudge.right', label: 'Nudge right 1 px', group: 'Transform', defaults: ['ArrowRight'], run: () => actions.nudge(1, 0) },
    { id: 'nudge.up', label: 'Nudge up 1 px', group: 'Transform', defaults: ['ArrowUp'], run: () => actions.nudge(0, -1) },
    { id: 'nudge.down', label: 'Nudge down 1 px', group: 'Transform', defaults: ['ArrowDown'], run: () => actions.nudge(0, 1) },
    { id: 'nudge.left-10', label: 'Nudge left 10 px', group: 'Transform', defaults: ['Shift+ArrowLeft'], run: () => actions.nudge(-10, 0) },
    { id: 'nudge.right-10', label: 'Nudge right 10 px', group: 'Transform', defaults: ['Shift+ArrowRight'], run: () => actions.nudge(10, 0) },
    { id: 'nudge.up-10', label: 'Nudge up 10 px', group: 'Transform', defaults: ['Shift+ArrowUp'], run: () => actions.nudge(0, -10) },
    { id: 'nudge.down-10', label: 'Nudge down 10 px', group: 'Transform', defaults: ['Shift+ArrowDown'], run: () => actions.nudge(0, 10) },
    { id: 'transform.fit', label: 'Fit to screen', group: 'Transform', defaults: mod('KeyF'), run: () => actions.fit('fit') },
    { id: 'transform.reset', label: 'Reset transform', group: 'Transform', defaults: ['Ctrl+Alt+KeyR'], run: () => actions.fit('reset') },

    { id: 'studio.toggle-mode', label: 'Toggle studio mode', group: 'Scenes', defaults: ['Ctrl+Alt+KeyS'], run: () => actions.toggleStudioMode() },
    { id: 'studio.transition', label: 'Transition', group: 'Scenes', defaults: ['Ctrl+Enter'], run: () => actions.transition() },

    { id: 'output.toggle-streaming', label: 'Start / stop streaming', group: 'Output', defaults: [], run: () => actions.toggleStreaming() },
    { id: 'output.toggle-recording', label: 'Start / stop recording', group: 'Output', defaults: [], run: () => actions.toggleRecording() },
    { id: 'output.toggle-pause', label: 'Pause / resume recording', group: 'Output', defaults: [], run: () => actions.togglePause() },
    { id: 'output.screenshot', label: 'Screenshot of program', group: 'Output', defaults: [], run: () => actions.screenshot() },
    { id: 'file.save', label: 'Save now', group: 'General', defaults: mod('KeyS'), run: () => actions.save() },
  ];
  for (let n = 1; n <= 9; n++) {
    list.push({
      id: `scene.select-${n}`,
      label: `Switch to scene ${n}`,
      group: 'Scenes',
      defaults: [`Digit${n}`],
      run: () => actions.selectSceneByIndex(n - 1),
    });
  }
  return list;
}
